// src/index.js or src/index.tsx
import './index.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {
    createBrowserRouter,
    Navigate,
    RouterProvider,
} from 'react-router-dom';
import { AuthProvider } from './AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';
import Dashboard from './routes/Dashboard';
import ErrorPage from './error-page';
import AuthLayout from './AuthLayout';
import Login from './routes/Login';
import LoginConfirm from './routes/LoginConfirm';
import { ServicesList } from './routes/services/ServicesList';
import { CreateService } from './routes/services/CreateService';
import { ManageServiceRoutes } from './routes/services/ManageServiceRoutes';
import { IPadsRoutes } from './routes/ipads/iPadsRoutes'
import { AddiPad } from './routes/ipads/AddiPad';
import { ManageIpadRoutes } from './routes/ipads/ManageIpadRoutes';
import { SignService } from './routes/sign/SignService';
import AnonSignLayout from './routes/sign/AnonSignLayout';
import { SignaturesRoutes } from './routes/signatures/SignaturesRoutes';
import { CreateBook } from './routes/books/CreateBook';
import { Books } from './routes/books/Books';
import { ManageBookRoutes } from './routes/books/ManageBookRoutes';
import { ManageAccountsRoutes } from './routes/management/ManageAccountsRoutes';
import { AnonFamilyLayout } from './routes/family/AnonFamilyLayout';
import { FamilyGuestbook } from './routes/family/FamilyGuestbook';
import AnonMemoryLayout from './routes/memory/AnonMemoryLayout';
import { ShareMemory } from './routes/memory/ShareMemory';
import Register from './routes/Register';
import ForgotPassword from './routes/ForgotPassword';
import ResetPassword from './routes/ResetPassword';
import { ManageSpecificAccountRoutes } from './routes/management/ManageSpecificAccountRoutes';
import { AdminRoutes } from './routes/admin/AdminRoutes';
import EmailCampaignsProtected from './routes/email/EmailCampaignsProtected';
import ReceivedEmailsProtected from './routes/emails/ReceivedEmailsProtected';
import ReceivedEmailDetail from './routes/emails/ReceivedEmailDetail';
import NotFound from './components/NotFound';
import { ServicesRoutes } from './routes/services/ServicesRoutes';
import { ServicesGraph } from './components/ServicesGraph';
import Welcome from './routes/Welcome';
import Supplies from './routes/Supplies';
import { QrCodesList } from './routes/qr-codes/QrCodesList';
import { CreateQrCode } from './routes/qr-codes/CreateQrCode';
import { ManageQrCode } from './routes/qr-codes/ManageQrCode';

const router = createBrowserRouter([
    {
        path: '/',
        element: <AuthLayout />,  // Protected Routes
        errorElement: <ErrorPage />,
        children: [
            {
                path: '/',
                element: <Navigate to="/signatures/list" />
            },
            {
                path: 'welcome',
                element: <Welcome />,
            },
            {
                path: 'supplies',
                element: <Supplies />,
            },
            {
                path: 'dashboard',
                element: <Dashboard />,
            },
            // Signatures
            {
                path: 'signatures',
                children: [
                    { index: true, element: <Navigate to="/signatures/list" replace /> },
                    { path: 'list', element: <SignaturesRoutes viewType="list" /> },
                    { path: 'dashboard', element: <SignaturesRoutes viewType="dashboard" /> },
                    { path: 'analytics', element: <SignaturesRoutes viewType="analytics" /> },
                    { path: 'report', element: <SignaturesRoutes viewType="report" /> },
                ]
            },
            // Services
            {
                path: 'services',
                children: [
                    { index: true, element: <Navigate to="/services/list" replace /> },
                    { path: 'list', element: <ServicesRoutes viewType="list" /> },
                    { path: 'dashboard', element: <ServicesRoutes viewType="dashboard" /> },
                ]
            },
            {
                path: 'services/:serviceUniqueId/*',
                element: <ManageServiceRoutes />,
            },
            {
                path: 'create-service',
                element: <CreateService />,
            },
            // Books
            {
                path: 'books',
                element: <Books />,
            },
            {
                path: 'create-book',
                element: <CreateBook />,
            },
            {
                path: 'books/:bookUniqueId/*',
                element: <ManageBookRoutes />,
            },
            // iPads
            {
                path: 'ipads',
                children: [
                    { index: true, element: <Navigate to="/ipads/list" replace /> },
                    { path: 'list', element: <IPadsRoutes viewType="list" /> },
                    { path: 'dashboard', element: <IPadsRoutes viewType="dashboard" /> },
                ]
            },
            {
                path: 'add-ipad',
                element: <AddiPad />,
            },
            {
                path: 'ipads/:ipadUniqueId/*',
                element: <ManageIpadRoutes />,
            },
            // Manage account
            {
                path: 'manage-account/*',
                element: <ManageSpecificAccountRoutes isForCurrentAccount />,
            },
            // Management
            {
                path: 'management/*',
                element: <ManageAccountsRoutes />,
            },
            // Admin routes
            {
                path: 'admin/*',
                element: <AdminRoutes />,
            },
            // Follow-up messages (per-account, opt-in)
            {
                path: 'follow-up-messages/*',
                element: <EmailCampaignsProtected />,
            },
            // Received emails (per-account, requires email follow-up enabled)
            {
                path: 'emails/received',
                element: <Navigate to="/emails/received/all" replace />,
            },
            {
                path: 'emails/received/all',
                element: <ReceivedEmailsProtected />,
            },
            {
                path: 'emails/received/threads',
                element: <ReceivedEmailsProtected />,
            },
            {
                path: 'emails/received/:id',
                element: <ReceivedEmailDetail />,
            },
            // QR Codes (FuneralLink)
            {
                path: 'qr-codes',
                element: <QrCodesList />,
            },
            {
                path: 'qr-codes/create',
                element: <CreateQrCode />,
            },
            {
                path: 'qr-codes/:unique_identifier',
                element: <ManageQrCode />,
            },
            // Catch-all 404 route
            {
                path: '*',
                element: <NotFound />,
            },
        ],
    },
    {
        path: '/sign',
        element: <AnonSignLayout />,
        errorElement: <ErrorPage />,
        children: [
            // Public signing route
            {
                path: ':serviceUniqueId',
                element: <SignService />
            },
        ],
    },
    {
        path: '/f', // For families
        element: <AnonFamilyLayout />,
        errorElement: <ErrorPage />,
        children: [
            // Public signing route
            {
                path: ':serviceUniqueId',
                element: <FamilyGuestbook />
            },
        ],
    },
    {
        path: '/memory', // For email campaign memory sharing
        element: <AnonMemoryLayout />,
        errorElement: <ErrorPage />,
        children: [
            {
                path: ':campaignTrackingId',
                element: <ShareMemory />
            },
        ],
    },
    {
        path: '/auth',
        errorElement: <ErrorPage />,
        children: [
            {
                path: 'login', // /auth/login
                element: <Login />,
            },
            {
                path: 'login-confirm', // /auth/login-confirm
                element: <LoginConfirm />,
            },
            {
                path: 'register', // /auth/register
                element: <Register />,
            },
            {
                path: 'forgot-password', // /auth/forgot-password
                element: <ForgotPassword />,
            },
            {
                path: 'reset-password', // /auth/reset-password?token=xxx
                element: <ResetPassword />,
            },
        ],
    },
]);

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <ErrorBoundary>
            <AuthProvider>
                <ToastProvider>
                    <RouterProvider router={router} />
                </ToastProvider>
            </AuthProvider>
        </ErrorBoundary>
    </React.StrictMode>
);
